import { Prism, Highlight, type RenderProps } from 'prism-react-renderer';
import { useEffect, useState, type JSX } from 'react';

import { prismDqlLanguage } from './prism-dql-language.js';
import { devLog } from '../../core/utils/logging.js';
import { DTPrismTheme } from '../../core/utils/prism-theme.js';

// Ensure Prism is globally available
(typeof global !== 'undefined' ? global : window).Prism = Prism;

Prism.languages.dql = prismDqlLanguage;

// Prism's built-in 'hash' token only matches 32/64-char hex strings, and the 'number'
// token only matches hex strings that start with a digit — leaving letter-leading 16-char
// hex IDs (e.g. trace/span IDs) unhighlighted. This fills that gap generically.
const SHORT_HEX_ID_TOKEN_NAME = 'strato-short-hash';
const SHORT_HEX_ID_PATTERN = /\b[a-f0-9]{16}\b/i;

/**
 *  Enhances the Prism 'log' language definition to include a token for short hex IDs.
 *  This is necessary because the default 'hash' and 'number' tokens do not cover letter-leading 16-char hex strings,
 *  which are common in log files (e.g. trace/span IDs). By adding a 'strato-short-hash' token, we ensure that these IDs are properly highlighted as constants.
 */
function enhancePrismLogLanguage(): void {
  const logLanguage = Prism.languages.log;

  if (!logLanguage || SHORT_HEX_ID_TOKEN_NAME in logLanguage) {
    return;
  }

  // Insert before 'property' because Prism's property pattern may consume
  // sequences like `span_id: <hex> trace_id:` as one token.
  Prism.languages.insertBefore('log', 'property', {
    [SHORT_HEX_ID_TOKEN_NAME]: {
      pattern: SHORT_HEX_ID_PATTERN,
      alias: 'constant',
    },
  });
}

/**
 * Supported languages for syntax highlighting.
 * @public
 */
export type HighlightLanguage =
  | 'markup'
  | 'bash'
  | 'clike'
  | 'c'
  | 'cpp'
  | 'css'
  | 'javascript'
  | 'jsx'
  | 'coffeescript'
  | 'actionscript'
  | 'go'
  | 'graphql'
  | 'json'
  | 'markdown'
  | 'objectivec'
  | 'python'
  | 'reason'
  | 'sql'
  | 'tsx'
  | 'typescript'
  | 'yaml'
  | 'dql'
  | 'log'
  | 'kotlin'
  | 'swift'
  | 'groovy';

interface HighlightExtendedProps {
  language?: HighlightLanguage;
  code: string;
  children: (props: RenderProps) => JSX.Element;
}

// Map of additional languages that need to be dynamically imported from PrismJS.
// These are not included in the 'prism-react-renderer' basic set of languages.
// With that, it ensures that the necessary syntax highlighting for additional languages
// is implemented when the Prism instance is available.
const additionalLanguageImports: Partial<
  Record<HighlightLanguage, () => Promise<void>>
> = {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  bash: () => import('prismjs/components/prism-bash.js'),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  log: () => import('prismjs/components/prism-log.js'),
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  groovy: () => import('prismjs/components/prism-groovy.js'),
};

/**
 * Extends the `Highlight` component from the Prism library.
 * This component can be used across Strato components to handle loading additional and custom languages.
 * @internal
 */
export const HighlightExtended = (props: HighlightExtendedProps) => {
  const { language, code, children } = props;
  const [loadedLanguages, setLoadedLanguages] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      Object.keys(additionalLanguageImports).map((lang) => [
        lang,
        Prism.languages[lang] !== undefined,
      ]),
    ),
  );

  // Dynamically loads additional Prism languages when needed.
  useEffect(() => {
    if (
      language &&
      additionalLanguageImports[language] &&
      !loadedLanguages[language]
    ) {
      additionalLanguageImports[language]()
        .then(() => {
          if (language === 'log') {
            enhancePrismLogLanguage();
          }
          setLoadedLanguages((prev) => ({ ...prev, [language]: true }));
        })
        .catch((error) => {
          devLog(`Failed to load language '${language}':`, error);
        });
    }
  }, [language, loadedLanguages]);

  // For the 'log' language, we need to ensure that the custom enhancement for short hex IDs is applied even if the language was already loaded (e.g. from a previous story).
  useEffect(() => {
    if (language === 'log' && loadedLanguages.log) {
      enhancePrismLogLanguage();
    }
  }, [language, loadedLanguages.log]);

  return (
    <Highlight
      language={language ?? 'plain'}
      code={code}
      theme={language === 'log' ? DTPrismTheme.Log : DTPrismTheme.Default}
      // @ts-expect-error language prop accepts limited set of languages that don't include `log`
      Prism={Prism}
    >
      {({ tokens, getLineProps, getTokenProps, className, style }) => (
        <>
          {children({
            tokens,
            getLineProps,
            getTokenProps,
            className,
            style,
          })}
        </>
      )}
    </Highlight>
  );
};
